// ===========================================
// Stripe Tab
// Payment processing configuration
// ===========================================

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
  useIntegrationOverview,
} from '../../../hooks/useIntegrations';
import IntegrationKeyManager from './IntegrationKeyManager';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import SetupGuideCard from './SetupGuideCard';
import { stripeSetupSteps, getWebhookUrl, docsLinks } from './setupGuides';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function StripeTab() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { data: settings, isLoading: settingsLoading } = useIntegrationSettings();
  const { data: overview, isLoading: overviewLoading } = useIntegrationOverview();
  const updateSettings = useUpdateIntegrationSettings();
  // Turning Stripe on or off is the owner's act, like its keys (roles R24; the server refuses others).
  const { isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();

  const stripeSettings = settings?.stripe;
  const stripeOverview = overview?.stripe;

  const handleToggleEnabled = async () => {
    if (!stripeSettings) return;
    await updateSettings.mutateAsync({
      stripe: { enabled: !stripeSettings.enabled },
    });
  };

  if (settingsLoading || overviewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stripeSettings || !stripeOverview) {
    return <Alert severity="error">Failed to load Stripe settings.</Alert>;
  }

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Stripe enables secure online payments for invoices, subscriptions, payment plans, and meet
        &amp; greet bookings.
      </Typography>

      {/* Setup Guide */}
      <SetupGuideCard
        integration="stripe"
        steps={stripeSetupSteps}
        webhookUrl={getWebhookUrl('stripe', schoolSlug)}
        docsLink={docsLinks.stripe}
      />

      {/* Per-School API Key Management */}
      <IntegrationKeyManager integration="stripe" />

      {/* Master Enable/Disable */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<PaymentIcon color={stripeSettings.enabled ? 'primary' : 'disabled'} />}
          title="Stripe Payments"
          subheader="Online payment processing"
          action={
            <Switch
              checked={stripeSettings.enabled}
              onChange={handleToggleEnabled}
              disabled={updateSettings.isPending || !stripeOverview.isConfigured || !isOwner}
            />
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {isOwner
              ? 'Enable or disable Stripe payment processing for your school.'
              : `Only your ${ownerTitle} can turn Stripe on or off.`}
          </Typography>
        </CardContent>
      </Card>

      {/* Configuration Details */}
      {stripeOverview.isConfigured && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Configuration Status" />
          <CardContent>
            <Stack spacing={2}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="API Keys Configured"
                    secondary="Secret and publishable keys are set"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {stripeOverview.webhookConfigured ? (
                      <CheckIcon color="success" fontSize="small" />
                    ) : (
                      <WarningIcon color="warning" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>Webhook</span>
                        <WebhookIcon fontSize="small" />
                      </Stack>
                    }
                    secondary={
                      stripeOverview.webhookConfigured
                        ? 'Webhook secret configured for real-time payment updates'
                        : 'Webhook not configured - payment status may not update automatically'
                    }
                  />
                </ListItem>
              </List>

              <Divider />

              {/* Features */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Enabled Features
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label="Invoice Payments" size="small" color="primary" variant="outlined" />
                  <Chip label="Subscriptions" size="small" color="primary" variant="outlined" />
                  <Chip label="Payment Plans" size="small" color="primary" variant="outlined" />
                  <Chip
                    label="Meet & Greet Payments"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card>
        <CardHeader title="About Stripe" />
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              Stripe is a secure payment processing platform that handles credit card payments,
              recurring subscriptions, and payment plans.
            </Typography>
            <Typography variant="body2">
              <strong>Supported payment methods:</strong> Credit/debit cards (Visa, Mastercard,
              American Express), Apple Pay, Google Pay.
            </Typography>
            <Typography variant="body2">
              <strong>Webhooks:</strong> Enable webhooks to receive real-time notifications when
              payments succeed, fail, or subscriptions change.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
